<!--
  employeeCardAvailabilities: availabilities row of the employee card dialog as week strip (AvailabilityWeek) with the same
  label filter, real-time matching ("Terminabgleich"), edit dialog and guards as the former employeeAvailabilitiesListItem.
  Props: employee
-->
<template>
  <div>
    <div v-if="employee.availabilities || (canReadAvailabilities && canUpdateAvailabilities)" class="fld">
      <span class="fld__icon"><v-icon>mdi-clock-outline</v-icon></span>
      <div style="min-width: 0">
        <div class="d-flex align-center" style="gap: 6px">
          <span class="fld__label">Verfügbarkeiten</span>
          <span v-if="loading" class="acq-tiny">· lädt …</span>
        </div>
        <div v-if="!employee.availabilities" class="fld__value fld__value--muted">Für diese Lehrkraft wurden keine Verfügbarkeiten hinterlegt</div>
        <template v-else>
          <div v-if="!filteredAvailabilities.length" class="fld__value fld__value--muted">Aktuell nicht verfügbar</div>
          <availability-week v-else :availabilities="filteredAvailabilities"/>
          <div v-if="expanded" class="d-flex flex-column mt-2" style="gap: 8px">
            <pill-group :items="labelItems" :value="labelSelection" label="Verfügbarkeiten filtern" @input="labelSelection = $event"/>
            <v-switch v-if="!disableRealTimeAvailabilities" v-model="useRealTimeAvailabilities" class="mt-0 pt-0" color="info" dense
                      hide-details inset label="Terminabgleich"/>
            <span v-if="!disableRealTimeAvailabilities" class="acq-tiny">Verfügbarkeiten werden mit Kursen, Schulungen usw. abgeglichen</span>
            <span v-if="updateDate" class="acq-tiny">Zuletzt aktualisiert am {{ moment(updateDate).format('DD.MM.YYYY [um] HH:mm [Uhr]') }}</span>
          </div>
          <button class="ecd-more" type="button" @click="expanded = !expanded">
            {{ expanded ? 'Weniger' : `${filterText} · Filter` }}
            <v-icon x-small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </button>
        </template>
      </div>
      <div class="fld__actions">
        <button v-if="employee.availabilities" v-can:update-employees="{ id: employee._id, keys: ['availabilities'] }"
                aria-label="Verfügbarkeiten bearbeiten" class="ibtn" type="button" v-tip="'Verfügbarkeiten bearbeiten'"
                @click="showUpdateAvailabilitiesDialog = true">
          <v-icon>mdi-pencil-outline</v-icon>
        </button>
        <button v-else aria-label="Verfügbarkeiten hinterlegen" class="ibtn ibtn--add" type="button" v-tip="'Verfügbarkeiten hinterlegen'"
                @click="showUpdateAvailabilitiesDialog = true">
          <v-icon>mdi-plus</v-icon>
        </button>
      </div>
    </div>
    <update-employee-availabilities-dialog v-model="showUpdateAvailabilitiesDialog" :employee-id="employee._id"
                                           @update:availabilities="handleAvailabilityUpdate"/>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import moment from 'moment';
import {Employee} from '@/interfaces/employee.interface';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import UpdateEmployeeAvailabilitiesDialog
  from '@/components/employees/employeeView/profileView/availabilitiesListItem/updateEmployeeAvailabilitiesDialog.vue';
import {AvailabilityInterface, LabeledAvailabilityInterface} from '@/interfaces/availability.interface';
import {AvailabilityLabels, AvailabilityLabelsVue} from '@/enums/AvailabilityLabels';
import {SchedulerApi} from '@/classes/api/scheduler.api.class';
import AvailabilityWeek from '@/components/common/profileUi/AvailabilityWeek.vue';
import PillGroup from '@/components/common/modern/pillGroup.vue';

export default Vue.extend({
  components: {AvailabilityWeek, PillGroup, UpdateEmployeeAvailabilitiesDialog},
  props: {
    employee: {
      type: Object as () => Employee,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    expanded: false,
    showUpdateAvailabilitiesDialog: false,
    labelSelection: [AvailabilityLabels.FOR_COURSES] as AvailabilityLabels[],
    useRealTimeAvailabilities: true,
    disableRealTimeAvailabilities: false,
    realTimeAvailabilities: [] as LabeledAvailabilityInterface[],
  }),
  computed: {
    moment: () => moment,
    labelItems: () => AvailabilityLabelsVue.map((label) => ({value: label.value, text: label.text})),
    filterText(): string {
      const texts = AvailabilityLabelsVue.filter((label) => this.labelSelection.includes(label.value)).map((label) => label.text);
      return texts.length ? texts.join(', ') : 'Alle';
    },
    filteredAvailabilities(): LabeledAvailabilityInterface[] {
      const availabilities = this.useRealTimeAvailabilities ? this.realTimeAvailabilities : this.employee.availabilities || [];
      if (this.labelSelection.length === 0) {
        return availabilities;
      }
      const preFiltered = availabilities.filter((availability: LabeledAvailabilityInterface) =>
          availability.labels.some((label: AvailabilityLabels) => this.labelSelection.includes(label)),
      ) || [];
      if (!this.labelSelection.includes(AvailabilityLabels.EXCEPTIONAL)) {
        return preFiltered.filter((el) => !el.labels.includes(AvailabilityLabels.EXCEPTIONAL));
      }
      return preFiltered;
    },
    canReadAvailabilities(): boolean {
      return this.$$getCrudEntity(CrudEntityTypes.EMPLOYEE, this.employee._id).canRead(EmployeeFields.AVAILABILITIES);
    },
    canUpdateAvailabilities(): boolean {
      return this.$$getCrudEntity(CrudEntityTypes.EMPLOYEE, this.employee._id).canUpdate(EmployeeFields.AVAILABILITIES);
    },
    updateDate(): Date | undefined {
      if (!this.employee?.availabilities) {
        return undefined;
      }
      const itemsWithTimestamp = this.employee.availabilities.filter((av) => !!av.updatedAt);
      if (itemsWithTimestamp.length === 0) {
        return undefined;
      }
      return new Date(Math.max(...itemsWithTimestamp.map((av) => new Date(av.updatedAt as Date).valueOf())));
    },
  },
  methods: {
    handleAvailabilityUpdate(availabilities: AvailabilityInterface[] | null) {
      this.$set(this.employee, 'availabilities', availabilities);
      this.initialize();
    },
    async initialize() {
      const entity = this.$$getCrudEntity(CrudEntityTypes.EMPLOYEE, this.employee._id);
      if (!entity.canRead(EmployeeFields.AVAILABILITIES)) {
        this.useRealTimeAvailabilities = false;
        this.disableRealTimeAvailabilities = true;
        return;
      }
      this.loading = true;
      try {
        const resp = await SchedulerApi.getAvailabilities({
          type: CrudEntityTypes.EMPLOYEE,
          id: this.employee._id,
        });
        this.realTimeAvailabilities = resp.availabilities;
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Echtzeitverfügbarkeiten - verwende statische Werte', 'error', e);
        this.useRealTimeAvailabilities = false;
        this.disableRealTimeAvailabilities = true;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.initialize();
    if (this.$route.query.instantUpdateAvailabilities === 'true') {
      this.showUpdateAvailabilitiesDialog = true;
    }
  },
  watch: {
    showUpdateAvailabilitiesDialog(v) {
      if (!v && this.$route.query.instantUpdateAvailabilities === 'true') {
        this.$router.push({
          query: {
            instantUpdateAvailabilities: 'false',
          },
        }).catch();
      }
    },
  },
});
</script>

<style scoped>
.ecd-more {
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12px;
  color: rgba(15, 23, 42, .55);
  cursor: pointer;
}

.ecd-more:hover {
  color: #1565C0;
}

.theme--dark .ecd-more {
  color: rgba(255, 255, 255, .6);
}
</style>
<style src="@/components/acquisitionList/ui/itemDetails.css"></style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
